import React, { ErrorInfo } from 'react'
import logger from 'services/logger'
import AppError from './AppError'

type Props = {
  children: React.ReactNode
  errorFallback?: React.ReactNode
}

type State = {
  error: Error | null
  errorInfo: ErrorInfo | null
}

/**
 * - Component to catch javascript errors anywhere in the child component tree
 * - Will log error to Datadog
 * - Show a fallback UI in the event of error, default is to use AppError component
 * - Only errors during rendering, in lifecycle methods, and in constructors of the child tree are captured
 * - Do not capture errors in event handlers, async code, server side rendering, and errors thrown in error boundary itself
 */
class ErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = {
      error: null,
      errorInfo: null,
    }
  }

  /**
   * - Will send error to DataDog for investigation
   */
  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    logger.errorInfo(errorInfo)
    this.setState({ error, errorInfo })
  }

  /**
   * - When error occurs, show error fallback UI if it is provided, otherwise show AppError component
   * - When no errors, show the children component as normal
   */
  render() {
    if (this.state.errorInfo) {
      return this.props.errorFallback || <AppError />
    }
    return this.props.children
  }
}

export default ErrorBoundary
